import { updateColumn } from '@api/customColumns.js';
import Popover from '@components/Common/Popover.js';
import toast from '@components/Common/Toast.js';
import Modal from '@components/Modals/Modal.js';
import { IS_MOBILE } from '@constants/global';
import {
  DotsVerticalIcon,
  EyeClosedIcon,
  EyeOpenIcon,
  GripIcon,
  PlusIcon,
  TrashIcon,
} from '@icons/index.js';
import { COLUMN_EVENT_TYPES, columnUpdate } from '@signals/project/index.js';
import { UIButton, UIDiv, UIInput, UIText } from '@ui';

export default class SelectOptionsModal extends Modal {
  constructor(column, currentFolderId = null) {
    super(
      i18next.t('pages.projects.modals.selectOptions.editTitle', {
        title: column.title,
      }),
      {
        showCancel: true,
        showConfirm: true,
        confirmText: i18next.t(
          'pages.projects.modals.selectOptions.saveChanges',
        ),
        cancelText: i18next.t('common.cancel'),
        modalContentClass: '!max-w-md',
        contentContainerClass: '!max-h-[70vh] !overflow-y-auto flex flex-col',
        handleOnAction: () => this.saveOptions(),
      },
    );

    this.column = column;
    this.currentFolderId = currentFolderId;
    this.selectOptions = this.parseOptions(column.options || {});
    this.hasChanges = false;
    this.content = this.render();

    // Callback to notify when options are updated
    this.onOptionsUpdated = null;
  }

  createModal() {
    const modal = super.createModal();
    this.contentContainer.add(this.content);

    return modal;
  }

  parseOptions(optionsData) {
    // Convert options to array format for easier manipulation
    return Object.entries(optionsData)
      .map(([key, value], index) => {
        if (typeof value === 'string') {
          return {
            id: key,
            label: value,
            color: '#C4C4C4',
            order: index,
            isActive: true,
          };
        } else {
          return {
            id: key,
            label: value.label || key,
            color: value.color || '#C4C4C4',
            order: value.order !== undefined ? value.order : index,
            isActive: !value.isDeactivated,
          };
        }
      })
      .sort((a, b) => a.order - b.order);
  }

  render() {
    const div = new UIDiv();
    // Options list container
    const listContainer = new UIDiv().setClass('flex-1 overflow-y-auto');
    this.optionsList = new UIDiv().setClass('space-y-2 py-2');

    this.renderOptions();
    listContainer.add(this.optionsList);

    // Add new option button
    const addButton = new UIButton().setClass(
      'w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 hover:border-gray-400 rounded-lg text-gray-600 hover:text-gray-700 transition-colors mt-4',
    );

    const addIcon = new PlusIcon({
      size: 16,
      color: 'none',
      strokeColor: 'currentColor',
    });
    addButton.add(addIcon);
    addButton.add(
      new UIText(i18next.t('pages.projects.modals.selectOptions.addNewLabel')),
    );

    addButton.onClick(() => this.addNewOption());

    div.add(listContainer, addButton);

    return div;
  }

  renderOptions() {
    this.optionsList.clear();

    this.selectOptions.forEach((option, index) => {
      const optionItem = this.createOptionItem(option, index);
      this.optionsList.add(optionItem);
    });

    // Set up drag and drop
    this.setupDragAndDrop();
  }

  createOptionItem(option, index) {
    const item = new UIDiv().setClass(
      'flex items-center gap-3 p-1.5 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors group',
    );
    item.setAttribute('data-option-id', option.id);

    // Grip handle (visible on hover)
    const gripHandle = new UIDiv().setClass(
      'flex-shrink-0 opacity-0 group-hover:opacity-100 cursor-move transition-opacity text-gray-400',
    );
    const gripIcon = new GripIcon({
      size: 16,
      color: 'none',
    });
    gripHandle.add(gripIcon);

    // Color dot
    const colorDot = new UIDiv().setClass(
      'relative w-6 h-6 rounded-full border-2 border-white shadow-sm cursor-pointer flex-shrink-0',
    );
    colorDot.setStyle('background-color', [option.color]);

    const colorInput = new UIInput().setClass(
      'w-full h-full absolute top-0 left-0 border-none p-0 m-0 opacity-0 cursor-pointer',
    );
    colorInput.setType('color');
    colorInput.setValue(option.color);
    colorDot.add(colorInput);

    colorInput.onInput(e => {
      const newColor = e.target.value;
      option.color = newColor;
      colorDot.setStyle('background-color', [newColor]);
      this.hasChanges = true;
    });

    // Prevent color input from closing the modal on click
    colorInput.onClick(e => {
      e.stopPropagation();
      this.options.canClose = false;
    });

    colorInput.onBlur(e => {
      e.stopPropagation();
      // make timeout to make the color input not close the modal immediately
      setTimeout(() => {
        this.options.canClose = true;
      }, 100);
    });

    // Label input
    const labelInput = new UIInput(option.label)
      .setClass(
        'cursor-text flex-1 bg-transparent hover:!border-gray-300 text-sm font-medium p-1 !shadow-none hover:bg-gray-50 transition-colors focus:outline-none focus:!ring-2 focus:!ring-[var(--o-hive-primary-80)]',
      )
      .setName(`option-label-${option.name}`)
      .setStyle('border', ['1px solid transparent']);

    if (!option.isActive) {
      labelInput.addClass('text-gray-400 line-through');
    }

    labelInput.onInput(e => {
      option.label = e.target.value;
      this.hasChanges = true;
    });

    // Three dots menu
    const menuButton = new UIDiv().setClass(
      'flex-shrink-0 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity text-gray-400 hover:text-gray-600 p-1',
    );

    const moreIcon = new DotsVerticalIcon({
      size: 16,
      color: 'none',
    });
    menuButton.add(moreIcon);

    // Create popover menu
    const menuContent = this.createOptionMenu(option, item);
    menuButton.onClick(e => {
      e.stopPropagation();
      Popover.show({
        trigger: menuButton,
        content: menuContent,
        placement: 'bottom-end',
        hideOnSelect: true,
        hideOnScroll: true,
      });
    });

    item.add(gripHandle, colorDot, labelInput, menuButton);

    // Make draggable
    if (!IS_MOBILE()) {
      item.dom.draggable = true;
    }
    this.setupItemDragEvents(item, option, gripHandle);

    return item;
  }

  createOptionMenu(option, item) {
    const menu = new UIDiv().setClass('py-1 min-w-32');

    // Toggle active/inactive
    const toggleButton = new UIDiv().setClass(
      'px-4 py-2 text-sm cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-2',
    );
    const toggleIcon = option.isActive
      ? new EyeClosedIcon({
          size: 18,
          color: 'none',
        })
      : new EyeOpenIcon({
          size: 18,
          color: 'none',
        });
    toggleButton.add(toggleIcon);
    toggleButton.add(new UIText(option.isActive ? 'Deactivate' : 'Activate'));
    toggleButton.onClick(() => {
      option.isActive = !option.isActive;
      this.hasChanges = true;
      this.renderOptions(); // Re-render to update visual state
    });

    // Delete option
    const deleteButton = new UIDiv().setClass(
      'px-4 py-2 text-sm cursor-pointer transition-colors flex items-center gap-2',
    );
    const deleteIcon = new TrashIcon({
      size: 18,
      color: 'none',
      strokeColor: 'black',
    });
    deleteButton.add(deleteIcon);
    deleteButton.add(new UIText('Delete'));
    deleteButton.onClick(() => {
      this.deleteOption(option.id);
    });

    menu.add(toggleButton, deleteButton);
    return menu;
  }

  addNewOption() {
    const newOption = {
      id: `option_${Date.now()}`,
      label: 'New Label',
      color: '#C4C4C4',
      order: this.selectOptions.length,
      isActive: true,
    };

    this.selectOptions.push(newOption);
    this.hasChanges = true;
    this.renderOptions();
  }

  deleteOption(optionId) {
    const index = this.selectOptions.findIndex(opt => opt.id === optionId);
    if (index > -1) {
      this.selectOptions.splice(index, 1);
      this.hasChanges = true;
      this.renderOptions();
    }
  }

  setupDragAndDrop() {
    this.optionsList.onDragOver(e => {
      e.preventDefault();
    });

    this.optionsList.onDrop(e => {
      e.preventDefault();
      this.handleDrop(e);
    });
  }

  setupItemDragEvents(item, option, gripHandle) {
    item.onDragStart(() => {
      item.addClass('opacity-50');
    });

    item.onDragEnd(() => {
      item.removeClass('opacity-50');
    });

    item.onDragOver(e => {
      e.preventDefault();
      const draggingElement = this.optionsList.dom.querySelector('.opacity-50');
      if (draggingElement && draggingElement !== item.dom) {
        const rect = item.dom.getBoundingClientRect();
        const midpoint = rect.top + rect.height / 2;

        if (e.clientY < midpoint) {
          item.dom.parentNode.insertBefore(draggingElement, item.dom);
        } else {
          item.dom.parentNode.insertBefore(
            draggingElement,
            item.dom.nextSibling,
          );
        }
      }
    });

    // Mobile touch support
    if (IS_MOBILE() && gripHandle) {
      let touchDragging = false;

      gripHandle.onTouchStart(e => {
        if (e.touches && e.touches.length === 1) {
          touchDragging = true;
          item.addClass('opacity-50');
        }
      });

      gripHandle.onTouchMove(e => {
        if (!touchDragging) return;
        e.preventDefault();
        // Find option item under finger
        const touch = e.touches[0];
        const el = document.elementFromPoint(touch.clientX, touch.clientY);
        if (!el) return;
        // Find parent option item
        const optionItem = el.closest('[data-option-id]');
        if (optionItem && optionItem !== item.dom) {
          // Move the dragged element visually
          const rect = optionItem.getBoundingClientRect();
          const midpoint = rect.top + rect.height / 2;
          if (touch.clientY < midpoint) {
            optionItem.parentNode.insertBefore(item.dom, optionItem);
          } else {
            optionItem.parentNode.insertBefore(
              item.dom,
              optionItem.nextSibling,
            );
          }
        }
      });

      gripHandle.onTouchEnd(e => {
        if (touchDragging) {
          item.removeClass('opacity-50');
          touchDragging = false;
          // Trigger reorder logic
          this.handleDrop();
        }
      });
    }
  }

  handleDrop() {
    Array.from(this.optionsList.dom.children).map((item, index) => {
      const optionId = item.getAttribute('data-option-id');
      const option = this.selectOptions.find(opt => opt.id === optionId);
      if (option) {
        option.order = index;
      }
      return optionId;
    });

    // Sort options by new order
    this.selectOptions.sort((a, b) => a.order - b.order);
    this.hasChanges = true;
  }

  async saveOptions() {
    if (!this.hasChanges) return true;

    try {
      // Store old options for the event
      const oldOptions = { ...this.column.options };

      // Convert options back to the expected format
      const formattedOptions = {};
      this.selectOptions.forEach(option => {
        formattedOptions[option.id] = {
          label: option.label,
          color: option.color,
          order: option.order,
          isDeactivated: !option.isActive,
        };
      });

      // Update the column via API
      await updateColumn(this.column.id, {
        options: formattedOptions,
      });

      // Update local column data
      this.column.options = formattedOptions;

      // Trigger column options update event to notify all SelectCell instances
      columnUpdate.dispatch({
        type: COLUMN_EVENT_TYPES.OPTIONS_UPDATED,
        columnId: this.column.id,
        currentFolderId: this.currentFolderId,
        column: this.column,
        oldOptions: oldOptions,
        newOptions: formattedOptions,
      });

      // Notify that options have been updated (kept for backward compatibility)
      if (this.onOptionsUpdated) {
        this.onOptionsUpdated();
      }

      toast.success(
        i18next.t('pages.projects.modals.selectOptions.updateSuccess'),
      );
      return true;
    } catch (error) {
      console.error('Failed to update column options:', error);
      toast.error(`Failed to update options: ${error.message}`);
      return false;
    }
  }
}
